import { Badge, Spinner } from "@design";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { ApiError, type ExtensionResponse, getExtensionOptions } from "@/api";
import { useAreaCrumbs } from "@/components/areaCrumbs";
import { PageBreadcrumbs } from "@/components/PageBreadcrumbs";
import { DefinitionList, PageSection } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";
import { useAdminCanEdit } from "../useAdminCanEdit";
import { AccessEditSection, EnabledToggle } from "./AccessEditor";
import { ConfigurationSection } from "./ConfigurationSection";
import { ConsentDefaultsSection } from "./ConsentDefaultsSection";
import { AccessSection, CredentialsSection, SkillsSection, ToolsSection } from "./IntegrationSections";
import { STATE_BADGE, stateOf, useIssueText } from "./setupStatus";
import { type IntegrationActions, useIntegrationActions } from "./useIntegrationActions";

function SetupSection({
  extension,
  actions,
}: Readonly<{ extension: ExtensionResponse; actions?: IntegrationActions }>) {
  const { t } = useTranslation("adminIntegrations");
  const issueText = useIssueText();
  const state = stateOf(extension);
  const issues = extension.setup?.issues ?? [];
  return (
    <PageSection
      title={t("detail.setup")}
      actions={
        <div className="flex items-start gap-4">
          <Badge variant={STATE_BADGE[state]}>{t(`state.${state}`)}</Badge>
          {actions && <EnabledToggle extension={extension} actions={actions} />}
        </div>
      }
    >
      {issues.length > 0 ? (
        <ul aria-label={t("detail.setup")} className="flex list-disc flex-col gap-1 pl-5">
          {issues.map((issue) => (
            <li key={`${issue.code}:${issue.key ?? ""}`}>
              <Text as="span" variant="body-sm" tone="secondary">
                {issueText(issue, extension)}
              </Text>
            </li>
          ))}
        </ul>
      ) : (
        <Text variant="body-sm" tone="secondary">
          {t(state === "off" ? "detail.off" : "detail.ready")}
        </Text>
      )}
    </PageSection>
  );
}

function OverviewSection({ extension }: Readonly<{ extension: ExtensionResponse }>) {
  const { t } = useTranslation("adminIntegrations");
  const items: Array<{ label: string; value: ReactNode }> = [
    { label: t("detail.type"), value: t(`detail.types.${extension.setup?.kind ?? "integration"}`) },
  ];
  if (extension.description) items.push({ label: t("detail.description"), value: extension.description });
  if (extension.documentationUrl) {
    items.push({
      label: t("detail.documentation"),
      value: (
        <a
          href={extension.documentationUrl}
          target="_blank"
          rel="noreferrer"
          className="btn-focus-ring inline-flex items-center gap-1 rounded text-content-primary underline underline-offset-2"
        >
          {t("detail.openDocs")}
          <ExternalLink size={14} aria-hidden />
        </a>
      ),
    });
  }
  return (
    <PageSection title={t("detail.overview")}>
      <DefinitionList items={items} />
    </PageSection>
  );
}

function EditableSections({ extension }: Readonly<{ extension: ExtensionResponse }>) {
  const actions = useIntegrationActions(extension);
  return (
    <>
      <SetupSection extension={extension} actions={actions} />
      <ConfigurationSection extension={extension} actions={actions} />
      <AccessEditSection extension={extension} actions={actions} />
      <ConsentDefaultsSection extension={extension} />
      <OverviewSection extension={extension} />
      <ToolsSection extension={extension} />
      <SkillsSection extension={extension} />
    </>
  );
}

/**
 * /admin/integrations/:uuid: one integration's setup, configuration, access, tools and skills.
 * Editable on desktop; phones get the read-only view like the rest of Admin.
 */
export function IntegrationDetailPage() {
  const { t } = useTranslation("adminIntegrations");
  const areas = useAreaCrumbs();
  const { uuid = "" } = useParams();
  const canEdit = useAdminCanEdit();
  const query = useQuery({ ...getExtensionOptions({ path: { ext_uuid: uuid } }), select: (r) => r.data });
  const extension = query.data;

  let body: ReactNode;
  if (query.isPending) body = <Spinner />;
  else if (query.error instanceof ApiError && query.error.status === 404) {
    body = (
      <Text tone="secondary" role="alert">
        {t("detail.notFound")}
      </Text>
    );
  } else if (!extension) {
    body = (
      <Text tone="error" role="alert">
        {t("loadFailed")}
      </Text>
    );
  } else if (canEdit) {
    body = <EditableSections extension={extension} />;
  } else {
    body = (
      <>
        <SetupSection extension={extension} />
        <OverviewSection extension={extension} />
        <ToolsSection extension={extension} />
        <SkillsSection extension={extension} />
        <CredentialsSection extension={extension} />
        <AccessSection extension={extension} />
      </>
    );
  }

  return (
    <div className="flex flex-col">
      <PageBreadcrumbs
        trail={[areas.admin, { label: t("title"), to: "/admin/integrations" }]}
        current={extension?.name ?? ""}
      />
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-8 px-4 py-6 md:px-8">
        {!canEdit && (
          <Text variant="caption" tone="secondary">
            {t("readOnlyNote")}
          </Text>
        )}
        {body}
      </div>
    </div>
  );
}
